import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { DemoModeProvider, useDemoMode } from "@/contexts/DemoModeContext";

// Pages
import LoginPage from "@/pages/LoginPage";
import BootSequence from "@/pages/BootSequence";
import Dashboard from "@/pages/Dashboard";
import SourcesPage from "@/pages/SourcesPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('token');
      if (savedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${savedToken}` }
          });
          setUser(response.data);
          setToken(savedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('cesm_boot_completed');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route (permet aussi l'accès en mode démo)
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { isDemoMode } = useDemoMode();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-marine-void flex items-center justify-center">
        <div className="text-marine-cyan font-mono animate-pulse">CHARGEMENT SYSTÈME...</div>
      </div>
    );
  }
  
  // Permettre l'accès en mode démo même sans utilisateur
  if (isDemoMode) {
    return children;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Vérifier si le boot a été fait dans cette session
  const bootCompleted = sessionStorage.getItem('cesm_boot_completed');
  if (!bootCompleted) {
    return <Navigate to="/boot" replace />;
  }
  
  return children;
};

// CRT Overlay Component
const CRTOverlay = () => (
  <div className="crt-overlay" aria-hidden="true" />
);

function App() {
  return (
    <DemoModeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-marine-void">
            <CRTOverlay />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/boot" element={<BootSequence />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/sources" 
                element={
                  <ProtectedRoute>
                    <SourcesPage />
                  </ProtectedRoute>
                } 
              />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#09090b',
                  border: '1px solid #1e293b',
                  color: '#e2e8f0',
                  fontFamily: 'JetBrains Mono, monospace',
                },
              }}
            />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </DemoModeProvider>
  );
}

export default App;
